/**
 * GLOBAL STATUS (LOT 5.5.3) — le bloc « Opérationnel » de la sidebar devient
 * l'indicateur d'état GLOBAL de Gestion. Règles gelées :
 *
 *   · DÉRIVÉ, jamais hardcodé — uniquement des faits que Gestion connaît
 *     (worker heartbeat, transport des services managés, fraîcheur).
 *   · SANTÉ TECHNIQUE ≠ ALERTES MÉTIER : « 1 retour en retard » (Parc) ne
 *     rend PAS la plateforme dégradée. Les sévérités métier (WARNING /
 *     ACTION_REQUIRED / CRITICAL) sont volontairement ignorées ici — elles
 *     restent signalées à part (« N alertes à traiter »).
 *   · AUCUN polling supplémentaire : ce module ne fait AUCUNE requête, il
 *     recalcule à partir du fetch /api/v1/dashboard que le shell fait déjà.
 *
 * Niveaux : OPERATIONAL < ATTENTION < DEGRADED ; OFFLINE = le navigateur ne
 * joint plus l'API Gestion elle-même (calculé côté client, forcément).
 */

export type GlobalLevel = 'OPERATIONAL' | 'ATTENTION' | 'DEGRADED' | 'OFFLINE'

/** État TECHNIQUE d'un service — le transport, jamais le métier. */
export type ServiceTechState = 'OK' | 'DEGRADED' | 'MAINTENANCE' | 'STALE' | 'DOWN' | 'NO_DATA' | 'NOT_CONFIGURED'

export interface StatusCardInput {
  service_key: string
  name: string
  state: string
  service_status: string | null
  stale: boolean
  seconds_since_success: number | null
}

export interface WorkerInput { active: boolean; seconds_since_seen: number }

export interface ServiceStatusLine {
  key: string
  name: string
  tech: ServiceTechState
  seconds_since_success: number | null
  /** Des dernières données fiables existent (affichage « LKG » si DOWN). */
  has_data: boolean
}

/** Résumé Infrastructure (LOT 8) — CALCULÉ CÔTÉ SERVEUR par le moteur
 *  d'observabilité ; la sidebar l'affiche tel quel, sans réinterpréter. */
export interface InfraSummaryInput {
  level: 'OPERATIONAL' | 'ATTENTION' | 'DEGRADED'
  total: number
  healthy: number
  attention: number
  lines: { key: string; label: string; state: string; state_label: string }[]
  groups?: { key: string; label: string; total: number; healthy: number }[]
}

export interface GlobalStatus {
  level: GlobalLevel
  services: ServiceStatusLine[]
  worker: WorkerInput | null
  /** Alertes métier NON ACQUITTÉES — information secondaire, jamais le niveau. */
  alerts: number
  infra: InfraSummaryInput | null
}

/** Transport seul : les sévérités métier retombent volontairement sur OK. */
export function techStateOf(card: StatusCardInput): ServiceTechState {
  if (card.state === 'NOT_CONFIGURED') return 'NOT_CONFIGURED'
  if (card.state === 'NO_DATA') return 'NO_DATA'
  if (card.service_status === 'DOWN' || card.state === 'DOWN') return 'DOWN'
  if (card.service_status === 'MAINTENANCE' || card.state === 'MAINTENANCE') return 'MAINTENANCE'
  if (card.stale || card.state === 'STALE') return 'STALE'
  if (card.service_status === 'DEGRADED') return 'DEGRADED'
  return 'OK'
}

export function computeGlobalStatus(input: {
  cards: StatusCardInput[]
  worker: WorkerInput | null
  alerts: number
  offline: boolean
  infra?: InfraSummaryInput | null
}): GlobalStatus {
  const services: ServiceStatusLine[] = input.cards.map((c) => ({
    key: c.service_key,
    name: c.name,
    tech: techStateOf(c),
    seconds_since_success: c.seconds_since_success,
    has_data: c.state !== 'NO_DATA' && c.state !== 'NOT_CONFIGURED',
  }))

  let level: GlobalLevel = 'OPERATIONAL'
  const attention = services.some((s) =>
    s.tech === 'STALE' || s.tech === 'NO_DATA' || s.tech === 'MAINTENANCE' || s.tech === 'DEGRADED')
  const workerDown = input.worker !== null && !input.worker.active
  if (attention || workerDown) level = 'ATTENTION'
  if (services.some((s) => s.tech === 'DOWN')) level = 'DEGRADED'
  // LOT 8 : le niveau Infrastructure vient DU SERVEUR (même moteur que
  // /infrastructure) — on le FUSIONNE, on ne le recalcule pas.
  const infra = input.infra ?? null
  if (infra?.level === 'ATTENTION' && level === 'OPERATIONAL') level = 'ATTENTION'
  if (infra?.level === 'DEGRADED') level = 'DEGRADED'
  if (input.offline) level = 'OFFLINE'

  return { level, services, worker: input.worker, alerts: input.alerts, infra }
}

export const LEVEL_LABELS: Record<GlobalLevel, string> = {
  OPERATIONAL: 'Opérationnel',
  ATTENTION: 'Attention',
  DEGRADED: 'Dégradé',
  OFFLINE: 'Indisponible',
}

export const TECH_LABELS: Record<ServiceTechState, string> = {
  OK: 'Opérationnel',
  DEGRADED: 'Dégradé',
  MAINTENANCE: 'En maintenance',
  STALE: 'Données anciennes',
  DOWN: 'Indisponible',
  NO_DATA: 'Aucune donnée reçue',
  NOT_CONFIGURED: 'Non configuré',
}

/** Fraîcheur en langage humain — jamais de timestamp brut (§34). */
export function fraicheur(seconds: number | null, stale: boolean): string | null {
  if (seconds === null) return null
  if (stale) {
    if (seconds < 120) return `Dernières données fiables il y a ${seconds} s`
    if (seconds < 7200) return `Dernières données fiables il y a ${Math.round(seconds / 60)} min`
    return `Dernières données fiables il y a ${Math.round(seconds / 3600)} h`
  }
  if (seconds < 10) return 'Actualisé à l’instant'
  if (seconds < 120) return `Actualisé il y a ${seconds} s`
  if (seconds < 7200) return `Actualisé il y a ${Math.round(seconds / 60)} min`
  return `Actualisé il y a ${Math.round(seconds / 3600)} h`
}

/* ─────────────────────────── SUPERVISION (Sonde, 21/10/2026) ─── */

/** GET /api/v1/supervision/etat — l'état de Sonde vu par Gestion (miroir). */
export interface SupervisionEtat {
  state: 'ok' | 'indisponible' | 'non_configuree'
  since: string | null
  shadow_mode: boolean | null
  shadow_release: string | null
  shadow_summary_fr: string | null
}

/** Libellé posé par le serveur sur une source que la supervision ne voit
 *  plus (catalog.ts) : la ligne se grise, elle ne rougit pas. */
export const SUPERVISION_UNAVAILABLE_LABEL = 'Supervision indisponible'

/**
 * COMPOSITION, pas un second calcul : Sonde injoignable (ou son miroir de plus
 * de 90 s) pèse ATTENTION — jamais DEGRADED, jamais OFFLINE. On ne sait plus
 * rien des équipements qu'elle surveille ; on ne les déclare pas en panne.
 * 'non_configuree' : rien ne change.
 */
export function withSupervision(status: GlobalStatus | null, sup: SupervisionEtat | null): GlobalStatus | null {
  if (!status || sup?.state !== 'indisponible') return status
  return status.level === 'OPERATIONAL' ? { ...status, level: 'ATTENTION' } : status
}

/** « Supervision indisponible depuis 22:41 » — heure de Paris, jamais l'instant brut. */
export function supervisionPhrase(sup: SupervisionEtat): string {
  if (!sup.since) return 'Supervision indisponible'
  const d = new Date(sup.since)
  if (Number.isNaN(d.getTime())) return 'Supervision indisponible'
  const hhmm = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).format(d)
  return `Supervision indisponible depuis ${hhmm}`
}

/** Le texte de Sonde, tel quel (jamais recalculé) — seulement en mode ombre. */
export function ombrePartielle(sup: SupervisionEtat | null): string | null {
  if (!sup || sup.state === 'non_configuree' || sup.shadow_mode !== true) return null
  const t = sup.shadow_summary_fr?.trim()
  return t ? t : null
}
